// src/pages/Administration/Settings/index.tsx
/**
 * `/Administration/Settings`: the two care modules' configuration, admin only.
 *
 * Deliberately built to match **`/settings`**, which is the house pattern for a configuration screen:
 * a sticky `Paper` header carrying the title and the actions, gradient `Tabs` with icons, and inside
 * each panel a set of bordered `Paper` cards that open with a `ThemeIcon` + title + one-line blurb over
 * a `Divider`. Controls carry their own `label` and `description` rather than hand-rolled text above
 * them, the way `settings/components/MenuPreferencesSection.tsx` does it.
 *
 * Everything comes from `@/components/ui`. Nothing here imports `@mantine/core`.
 *
 * **Not a table.** Settings are named values an admin occasionally adjusts, so each one is an input,
 * one per row and **full width**. A forced two-column grid was tried and dropped: settings vary in label
 * length and control width, so pairing them left ragged rows and mismatched controls side by side.
 *
 * ⚠️ Controls are full width for a reason beyond looks: `description` is rendered **inside** the control,
 * so a narrow control wraps its help text into a matching narrow column. Constraining the input width
 * (a 180px `NumberInput` was tried) shreds the description into three lines. Same reason the boolean's
 * `Switch` takes `flex: 1`.
 *
 * Each module panel splits into **three vertical tabs**:
 *
 * 1. **General** — the flat key/value settings, minus the deletion rows.
 * 2. **Notification Channels** — one card per notification type, from its own service. See
 *    `NotificationChannelsTab.tsx`; ⚠️ those rows **save on every toggle**, not from the Save button
 *    here, because each is its own record with its own write.
 * 3. **Deletion** — the `AllowDelete.*` / `AllowBulkDelete.*` toggles.
 *
 * ⚠️ General and Deletion share **one** edit buffer and **one** Save button. A change made under
 * Deletion is saved from either tab, which is why the unsaved badge names the module rather than
 * looking like it belongs to the tab you happen to be on.
 *
 * Grouping and control choice both live in `settingGroups.ts`, inferred from the row because the API
 * exposes no schema. Read that file before changing either. The tab split keys off the same group ids,
 * so the two cannot disagree about what counts as a deletion setting.
 *
 * ⚠️ **Edit only, deliberately.** Both services extend `CrudService` and so expose `create` and
 * `delete`, but `UpdateACCareSettingRequest` / `UpdateACCourseSettingRequest` carry only `value`,
 * `description` and `isActive`. `name` is not updatable, which says these rows are **seeded by the
 * backend** and looked up by key. Inventing a key would write a row nothing reads, and deleting one the
 * module expects would break it. `description` is the field's help text, not an input: it documents the
 * key, it is not configuration.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  IconAlertTriangle, IconBell, IconBellOff, IconClock, IconDeviceFloppy, IconDots, IconEye,
  IconEyeOff, IconHeartHandshake, IconRefresh, IconSchool, IconSettings, IconWorld, IconTrash,
} from '@tabler/icons-react';
import {
  Alert, Badge, Box, Button, Divider, Group, Menu, NumberInput, Paper, SearchInput, Skeleton, Stack,
  Switch, Text, TextInput, ThemeIcon, Title,
} from '@/components/ui';
import Tabs from '@/components/ui/navigation/Tabs';
import { toast } from '@/components/ui/providers/AlertProvider';
import {
  useACCareNotificationChannelService, useACCareSettingService,
  useACCourseNotificationChannelService, useACCourseSettingService,
} from '@actschurch/ccms-sdk';
import NotificationChannelsTab, { type ChannelService } from './NotificationChannelsTab';
import {
  SETTING_GROUPS, controlKindOf, groupIdOf, humaniseKey, isTruthyValue, writeBoolean,
  type GroupId,
} from './settingGroups';
import { AccessControl } from '@/components';

/** The fields both module settings share, and all this page touches. */
interface SettingRow {
  settingID: number;
  name: string;
  value?: string | null;
  description?: string | null;
  isActive: boolean;
}

/** The editable pair. `description` is deliberately not here, see the file header. */
interface SettingEdit {
  value: string;
  isActive: boolean;
}

/** Structural type both setting services satisfy, so the panel does not care which module it serves. */
interface SettingService {
  getAll: () => Promise<SettingRow[]>;
  update: (id: number, data: { value: string; isActive: boolean }) => Promise<unknown>;
}

const GROUP_ICON: Record<GroupId, typeof IconWorld> = {
  deletion: IconTrash,
  access: IconWorld,
  timing: IconClock,
  notifications: IconBell,
  other: IconSettings,
};

interface SettingFieldProps {
  row: SettingRow;
  edit: SettingEdit;
  onChange: (patch: Partial<SettingEdit>) => void;
}

/** One setting, as a labelled control. The control is chosen from the value, see `settingGroups.ts`. */
function SettingField({ row, edit, onChange }: SettingFieldProps) {
  const kind = controlKindOf(row.value);
  const label = humaniseKey(row.name);
  const isDirty = edit.value !== (row.value ?? '') || edit.isActive !== (row.isActive ?? true);
  const description = row.description ?? undefined;

  /**
   * `isActive` is a soft-delete flag on the row, not configuration, and it is a **different concept**
   * from a boolean value. Rendered as a second switch beside one, the two read as the same thing, so it
   * lives in an overflow instead. An inactive setting still carries a badge and dims, so it stays visible.
   */
  const markers = (
    <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
      {!edit.isActive && <Badge size="xs" variant="light" color="gray">Not in use</Badge>}
      {isDirty && <Badge size="xs" variant="light" color="orange">Unsaved</Badge>}
      <Menu position="bottom-end" withinPortal>
        <Menu.Target>
          <Button variant="subtle" color="gray" size="compact-xs" px={4} aria-label={`${label} options`}>
            <IconDots size={14} />
          </Button>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item
            leftSection={edit.isActive ? <IconEyeOff size={14} /> : <IconEye size={14} />}
            onClick={() => onChange({ isActive: !edit.isActive })}
          >
            {edit.isActive ? 'Mark as not in use' : 'Mark as in use'}
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </Group>
  );

  // A boolean reads best with the switch on the label line, matching /settings.
  if (kind === 'boolean') {
    return (
      <Box style={{ opacity: edit.isActive ? 1 : 0.55 }}>
        <Group justify="space-between" align="flex-start" wrap="nowrap" gap="sm">
          <Switch
            style={{ flex: 1 }}
            label={label}
            description={description}
            checked={isTruthyValue(edit.value)}
            disabled={!edit.isActive}
            onChange={(e) => onChange({ value: writeBoolean(e.currentTarget.checked, row.value) })}
          />
          {markers}
        </Group>
      </Box>
    );
  }

  return (
    <Box style={{ opacity: edit.isActive ? 1 : 0.55 }}>
      <Group justify="space-between" align="center" wrap="nowrap" gap="sm" mb={4}>
        <Text size="sm" fw={500}>{label}</Text>
        {markers}
      </Group>
      {kind === 'number' ? (
        <NumberInput
          aria-label={label}
          description={description}
          min={0}
          value={edit.value === '' ? '' : Number(edit.value)}
          disabled={!edit.isActive}
          onChange={(v) => onChange({ value: v === '' ? '' : String(v) })}
        />
      ) : (
        <TextInput
          aria-label={label}
          description={description}
          type={kind === 'url' ? 'url' : 'text'}
          placeholder="Not set"
          value={edit.value}
          disabled={!edit.isActive}
          onChange={(e) => onChange({ value: e.currentTarget.value })}
        />
      )}
    </Box>
  );
}

interface ModulePanelProps {
  service: SettingService;
  moduleLabel: string;
  /** The module's notification channel service, for the Notification Channels tab. */
  channelService: ChannelService;
  search: string;
}

function ModulePanel({ service, moduleLabel, channelService, search }: ModulePanelProps) {
  const [rows, setRows] = useState<SettingRow[]>([]);
  const [edits, setEdits] = useState<Record<number, SettingEdit>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const result = (await service.getAll()) ?? [];
      setRows(result);
      // The working copy is rebuilt on every load, so Reload genuinely discards unsaved edits.
      setEdits(Object.fromEntries(
        result.map((r) => [r.settingID, { value: r.value ?? '', isActive: r.isActive ?? true }]),
      ));
    } catch (e) {
      console.error(`[Settings] failed to load ${moduleLabel} settings:`, e);
      setLoadError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [service, moduleLabel]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const setField = (id: number, patch: Partial<SettingEdit>) =>
    setEdits((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  /** Only the rows whose value or active flag actually differs from what the server returned. */
  const dirtyRows = useMemo(
    () => rows.filter((r) => {
      const edit = edits[r.settingID];
      if (!edit) return false;
      return edit.value !== (r.value ?? '') || edit.isActive !== (r.isActive ?? true);
    }),
    [rows, edits],
  );

  const term = search.trim().toLowerCase();
  const visible = useMemo(() => (term
    ? rows.filter((r) =>
        r.name.toLowerCase().includes(term) ||
        // ⚠️ The **label** is searched as well as the raw key. Keys are camel case, so searching the
        // words actually on screen — "bulk delete" — matched nothing against `AllowBulkDelete.Intakes`
        // and the setting looked absent rather than unmatched.
        humaniseKey(r.name).toLowerCase().includes(term) ||
        (r.value ?? '').toLowerCase().includes(term) ||
        (r.description ?? '').toLowerCase().includes(term))
    : rows), [rows, term]);

  /** Cards, in the order declared in `settingGroups.ts`, with empty ones dropped. */
  const grouped = useMemo(
    () => SETTING_GROUPS
      .map((group) => ({ group, items: visible.filter((r) => groupIdOf(r.name) === group.id) }))
      .filter((g) => g.items.length > 0),
    [visible],
  );

  /**
   * The two flat-settings tabs.
   *
   * ⚠️ Split by group id, not by a second classifier — `settingGroups.ts` already decides what is a
   * deletion setting, and duplicating that rule here would let the tab and the card disagree.
   */
  const generalGroups = useMemo(() => grouped.filter((g) => g.group.id !== 'deletion'), [grouped]);
  const deletionGroups = useMemo(() => grouped.filter((g) => g.group.id === 'deletion'), [grouped]);

  /**
   * The module master switch, read from its own row.
   *
   * ⚠️ Read from `rows`, not from the edit buffer: this drives a banner describing what the **server**
   * is doing, and an unsaved toggle has not changed that yet.
   */
  const notificationsPaused = useMemo(() => {
    const master = rows.find(
      (r) => (r.name ?? '').trim().toLowerCase() === 'notificationsenabled' && r.isActive !== false,
    );
    return master !== undefined && !isTruthyValue(master.value ?? '');
  }, [rows]);

  const handleSave = async () => {
    if (dirtyRows.length === 0) return;
    setSaving(true);
    try {
      // One call per changed row, sequential so a mid-way failure leaves a comprehensible state and the
      // reload below shows exactly what landed.
      for (const row of dirtyRows) {
        await service.update(row.settingID, edits[row.settingID]);
      }
      toast.success(
        'Settings saved',
        `${dirtyRows.length} ${moduleLabel} setting${dirtyRows.length === 1 ? '' : 's'} updated.`,
      );
      await fetchAll();
    } catch (e) {
      console.error('[Settings] save failed:', e);
      toast.error('Could not save', e instanceof Error ? e.message : String(e));
      // Reload regardless, so the form reflects what the server holds after a partial write.
      await fetchAll();
    } finally {
      setSaving(false);
    }
  };

  if (loadError) {
    return (
      <Alert
        icon={<IconAlertTriangle size={16} />}
        type="error"
        title={`Could not load the ${moduleLabel} settings`}
      >
        <Stack gap="xs" align="flex-start">
          <Text size="sm">{loadError}</Text>
          <Button size="xs" variant="light" onClick={fetchAll}>Try again</Button>
        </Stack>
      </Alert>
    );
  }

  /**
   * The flat key/value settings, rendered for one subset of groups.
   *
   * Shared by General and Deletion so the two cannot drift: the action bar, the dirty count and the
   * save path are one implementation seen through two filters, not two copies.
   */
  const flatPanel = (
    groups: typeof grouped,
    emptyMessage: string | null,
  ) => {
    const shownCount = groups.reduce((n, g) => n + g.items.length, 0);
    return (
    <Stack gap="lg">
      {/* Module action bar. Each module saves on its own, because each row is its own update call.
          ⚠️ The count is for this tab, but the unsaved badge and Save are **module-wide** — one edit
          buffer covers both flat tabs, so a change made under Deletion is saved from either. The badge
          says so rather than letting a count on General look like it belongs to General. */}
      <Group justify="space-between" align="center" wrap="wrap">
        <Group gap="xs" align="center">
          <Text size="sm" c="dimmed">
            {shownCount} setting{shownCount === 1 ? '' : 's'} here
          </Text>
          {dirtyRows.length > 0 && (
            <Badge size="sm" variant="light" color="orange">
              {dirtyRows.length} unsaved in {moduleLabel}
            </Badge>
          )}
        </Group>
        <Group gap="sm">
          <Button
            variant="default"
            size="sm"
            leftSection={<IconRefresh size={16} />}
            onClick={fetchAll}
            loading={loading}
          >
            Reload
          </Button>
          <Button
            size="sm"
            leftSection={<IconDeviceFloppy size={16} />}
            onClick={handleSave}
            loading={saving}
            disabled={dirtyRows.length === 0}
          >
            Save changes
          </Button>
        </Group>
      </Group>

      {loading && rows.length === 0 ? (
        <Stack gap="lg">
          {Array.from({ length: 2 }).map((_, card) => (
            <Paper key={card} p="lg" radius="md" withBorder>
              <Skeleton height={16} width="26%" mb="lg" radius="sm" />
              <Stack gap="lg">
                {Array.from({ length: 3 }).map((__, i) => (
                  <Stack key={i} gap={6}>
                    <Skeleton height={12} width="30%" radius="sm" />
                    <Skeleton height={36} radius="sm" />
                  </Stack>
                ))}
              </Stack>
            </Paper>
          ))}
        </Stack>
      ) : emptyMessage ? (
        <Paper p="xl" radius="md" withBorder>
          <Text size="sm" c="dimmed" ta="center">{emptyMessage}</Text>
        </Paper>
      ) : (
        groups.map(({ group, items }) => {
          const Icon = GROUP_ICON[group.id];
          return (
            <Paper key={group.id} p="lg" radius="md" withBorder>
              <Group gap="sm" mb="md" wrap="nowrap">
                <ThemeIcon size="lg" radius="md" variant="light" color={group.color}>
                  <Icon size={20} />
                </ThemeIcon>
                <Box>
                  <Text fw={600}>{group.title}</Text>
                  <Text size="xs" c="dimmed">{group.blurb}</Text>
                </Box>
              </Group>

              <Divider mb="lg" />

              <Stack gap="lg">
                {items.map((row) => (
                  <SettingField
                    key={row.settingID}
                    row={row}
                    edit={edits[row.settingID] ?? { value: '', isActive: true }}
                    onChange={(patch) => setField(row.settingID, patch)}
                  />
                ))}
              </Stack>
            </Paper>
          );
        })
      )}
    </Stack>
    );
  };

  return (
    <Tabs
      orientation="vertical"
      defaultValue="general"
      keepMounted={false}
      /**
       * Pills, so the selected tab is filled rather than marked by a hairline.
       *
       * ⚠️ The default vertical variant shows selection with a 2px edge on the list's own border —
       * which reads as a focus ring, not a selection, and disappears entirely once the browser draws
       * a real focus ring over it.
       */
      tabsVariant="pills"
      styles={{
        // The list sits inside its own card below, so its built-in divider would double the border.
        list: { border: 'none', gap: 4, width: '100%' },
        // Full-width, left-aligned tabs: a centred label in a 200px column leaves the icons in a
        // ragged line, which is what made the column look misaligned with the panel beside it.
        tab: { width: '100%', justifyContent: 'flex-start' },
        panel: { flex: 1, minWidth: 0 },
      }}
    >
      {/* ⚠️ `alignSelf: flex-start` matters: a vertical `Tabs` stretches its children, so without it
          this card runs the full height of whichever panel is open and reads as a page divider. */}
      <Paper
        withBorder
        radius="md"
        p="xs"
        style={{ width: 220, flexShrink: 0, alignSelf: 'flex-start' }}
      >
        <Tabs.List>
          <Tabs.Tab value="general" leftSection={<IconSettings size={16} />}>General</Tabs.Tab>
          <Tabs.Tab
            value="channels"
            leftSection={notificationsPaused ? <IconBellOff size={16} /> : <IconBell size={16} />}
          >
            Notification Channels
          </Tabs.Tab>
          <AccessControl roles={['Admin']} mode='hide'>
            <Tabs.Tab value="deletion" leftSection={<IconTrash size={16} />}>Deletion</Tabs.Tab>
          </AccessControl>
        </Tabs.List>
      </Paper>

      <Tabs.Panel value="general" pl="lg">
        <Stack gap="lg">
          {/* Says plainly what the master switch is doing, rather than leaving it as one row among
              many that happens to govern every other notification setting on the page. */}
          {notificationsPaused && (
            <Alert type="warning" icon={<IconBellOff size={16} />}>
              <Text size="sm">
                All member and staff notifications for {moduleLabel} are paused. Nothing sends, whatever
                the individual topics under Notification Channels say.
              </Text>
            </Alert>
          )}
          {flatPanel(generalGroups, rows.length === 0
            ? `No ${moduleLabel} settings yet.`
            : generalGroups.length === 0 ? 'No settings match that search.' : null)}
        </Stack>
      </Tabs.Panel>

      <Tabs.Panel value="channels" pl="lg">
        <NotificationChannelsTab
          service={channelService}
          moduleLabel={moduleLabel}
          search={search}
          notificationsPaused={notificationsPaused}
        />
      </Tabs.Panel>

      <Tabs.Panel value="deletion" pl="lg">
        {flatPanel(deletionGroups, rows.length === 0
          ? `No ${moduleLabel} settings yet.`
          : deletionGroups.length === 0
            ? 'No deletion settings for this module. They appear once the backend seeds them.'
            : null)}
      </Tabs.Panel>
    </Tabs>
  );
}

export default function AdministrationSettingsPage() {
  const careSettings = useACCareSettingService();
  const courseSettings = useACCourseSettingService();
  const careChannels = useACCareNotificationChannelService();
  const courseChannels = useACCourseNotificationChannelService();
  const [search, setSearch] = useState('');

  return (
    <Stack gap="lg">
      {/* Sticky header, matching /settings. */}
      <Paper
        py="md"
        px="lg"
        radius="md"
        withBorder
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          backgroundColor: 'var(--mantine-color-body)',
        }}
      >
        <Group justify="space-between" align="center" wrap="wrap" gap="md">
          <Box>
            <Title order={2}>Module settings</Title>
            <Text size="sm" c="dimmed" mt={4}>
              Configuration for Counselling and Grow. A change takes effect wherever the module reads
              that key.
            </Text>
          </Box>
          <SearchInput
            placeholder="Search settings"
            value={search}
            onChange={setSearch}
            onClear={() => setSearch('')}
            radius="md"
            size="sm"
            w={280}
          />
        </Group>
      </Paper>

      <Tabs tabsVariant="gradient" defaultValue="counselling" keepMounted={false}>
        <Tabs.List mb="lg">
          <Tabs.Tab value="counselling" leftSection={<IconHeartHandshake size={16} />}>
            Counselling
          </Tabs.Tab>
          <Tabs.Tab value="grow" leftSection={<IconSchool size={16} />}>
            Grow
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="counselling">
          <ModulePanel
            service={careSettings}
            channelService={careChannels}
            moduleLabel="Counselling"
            search={search}
          />
        </Tabs.Panel>
        <Tabs.Panel value="grow">
          <ModulePanel
            service={courseSettings}
            channelService={courseChannels}
            moduleLabel="Grow"
            search={search}
          />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}
